import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN, REFRESH_TOKEN, USER_INFO } from "constants/AuthConstant";
import { signOutSuccess } from "store/slices/authSlice";
import store from "../store";
import { notification } from "antd";
import { resetWsAuth, closeWS } from "../wsClient";

const apiCall = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
    refreshToken,
  });

  const { accessToken, refreshToken: newRefreshToken, user } = response.data;

  localStorage.setItem(AUTH_TOKEN, accessToken);
  if (newRefreshToken) {
    localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
  }
  if (user) {
    localStorage.setItem(USER_INFO, JSON.stringify(user));
  }

  resetWsAuth();
  return accessToken;
};

const handleLogout = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem(USER_INFO);
  closeWS();
  store.dispatch(signOutSuccess());
};

apiCall.interceptors.request.use(
  (config) => {
    if (config.skipAuth) {
      return config;
    }

    const token =
      typeof window !== "undefined" ? localStorage.getItem(AUTH_TOKEN) : null;

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiCall.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (error.response?.data?.code === "TOKEN_EXPIRED") {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return apiCall(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          processQueue(null, newToken);
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return apiCall(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          notification.error({
            message: "Session Expired",
            description: "Please login again",
          });
          handleLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      notification.error({
        message: "Authentication Failed",
        description: "Please login again",
      });
      handleLogout();
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      notification.error({
        message: "Access Denied",
        description:
          error.response?.data?.message ||
          "You do not have permission to perform this action",
      });
      return Promise.reject(error);
    }

    if (!axios.isCancel(error) && error.response) {
      const status = error.response.status;
      let message = "Error";
      if (status === 404) message = "Not Found";
      else if (status === 500) message = "Internal Server Error";
      else if (status === 508) message = "Request Timeout";

      notification.error({
        message,
        description: error.response?.data?.message || error.message,
      });
    }

    return Promise.reject(error);
  }
);

// Proactive token refresh - refresh every 90 minutes to avoid expiry
const REFRESH_INTERVAL = 90 * 60 * 1000;
let refreshTimer = null;

const startProactiveRefresh = () => {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(async () => {
    const token = localStorage.getItem(AUTH_TOKEN);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    if (token && refreshToken && !isRefreshing) {
      try {
        await refreshAccessToken();
        console.log('[Auth] Token proactively refreshed');
      } catch (err) {
        console.warn('[Auth] Proactive refresh failed:', err.message);
      }
    }
  }, REFRESH_INTERVAL);
};

// Start proactive refresh if user is already logged in
if (localStorage.getItem(AUTH_TOKEN) && localStorage.getItem(REFRESH_TOKEN)) {
  startProactiveRefresh();
}

export { startProactiveRefresh };
export default apiCall;
